// Custom hook for managing notifications/toast messages

import { useState, useCallback } from 'react';

/**
 * Custom hook for managing notifications
 * @returns {object} - Notification state and methods
 */
const useNotification = () => {
  const [notification, setNotification] = useState(null);

  /**
   * Show a notification
   * @param {string} message - The message to display
   * @param {string} type - Type of notification: 'success', 'error', 'info', 'warning'
   * @param {number} duration - Duration in milliseconds (default: 3000)
   */
  const showNotification = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now();

    setNotification({
      id,
      message,
      type,
      isVisible: true,
    });

    // Auto-hide after duration
    setTimeout(() => {
      hideNotification();
    }, duration);
  }, []);

  /**
   * Hide the current notification
   */
  const hideNotification = useCallback(() => {
    setNotification((prev) => {
      if (!prev) return null;
      return { ...prev, isVisible: false };
    });

    // Remove from state after fade-out animation
    setTimeout(() => {
      setNotification(null);
    }, 300);
  }, []);

  /**
   * Show success notification
   */
  const showSuccess = useCallback((message, duration) => {
    showNotification(message, 'success', duration);
  }, [showNotification]);

  /**
   * Show error notification
   */
  const showError = useCallback((message, duration) => {
    showNotification(message, 'error', duration);
  }, [showNotification]);

  /**
   * Show info notification
   */
  const showInfo = useCallback((message, duration) => {
    showNotification(message, 'info', duration);
  }, [showNotification]);

  /**
   * Show warning notification
   */
  const showWarning = useCallback((message, duration) => {
    showNotification(message, 'warning', duration);
  }, [showNotification]);

  return {
    notification,
    showNotification,
    hideNotification,
    showSuccess,
    showError,
    showInfo,
    showWarning,
  };
};

export default useNotification;
